// Globals
import { useTranslation } from 'react-i18next';

// Models
import { ROUTES } from '@/models/router.model';
import { IPartnerProfile } from '@/models/partners.model';

// Modules
import { usePermissions, Resource, Permission } from '@/modules/Permissions';

// Components
import { StyledActionsWrap, StyledButton, StyledStatusButton } from './PartnerTableActionsStyles';
import {
  CrossIcon,
  EditIcon,
  EyeIcon,
  CloseEyeIcon,
  CloseFullCircleIcon,
  CheckmarkCircleIcon,
  ResendIcon,
} from '@/icons';
import { StyledActionButton } from '../Styled';
import { COLORS } from '@/theme';
import { Link } from '@/ui';

type TPartnerTableActions = {
  id: string;
  isVisible: boolean;
  isActive: boolean;
  record: IPartnerProfile;
  setPartnerForDelete: (partner: IPartnerProfile) => void;
  setForStatusChange: (partner: IPartnerProfile) => void;
  setHidePartner: (partner: IPartnerProfile) => void;
  authAsPartner: (id: string) => void;
  onResendInvitation: (id: string) => void;
};

const PartnerTableActions = ({
  id,
  isVisible,
  isActive,
  setPartnerForDelete,
  setForStatusChange,
  setHidePartner,
  record,
  authAsPartner,
  onResendInvitation,
}: TPartnerTableActions) => {
  const { t } = useTranslation(['usersList']);
  const { canManage, can } = usePermissions();
  const canManagePartners = canManage(Resource.PARTNER);
  const canLoginAsPartner = can(Permission.PARTNER_LOGIN_AS);
  const userStatusButtonText = isActive ? 'users_list_block_button' : 'users_list_unblock_button';

  const handleDelete = () => setPartnerForDelete(record);

  const handleStatusChange = () => setForStatusChange(record);

  const handleHide = () => setHidePartner(record);

  const handleResend = () => onResendInvitation(id);

  return (
    <StyledActionsWrap>
      {canManagePartners && (
        <>
          {record.invitation_expired && (
            <StyledActionButton $color={COLORS.BLUE} onClick={handleResend}>
              <ResendIcon />
            </StyledActionButton>
          )}
          <Link path={`${ROUTES.PARTNER_DETAILS_INDEX}/${id}`}>
            <StyledActionButton $color={COLORS.GREEN}>
              <EditIcon />
            </StyledActionButton>
          </Link>
          <StyledActionButton $color={COLORS.RED} onClick={handleDelete}>
            <CrossIcon />
          </StyledActionButton>
          <StyledActionButton $color={isVisible ? COLORS.PALE_YELLOW : COLORS.SOFT_GRAY} onClick={handleHide}>
            {isVisible ? <EyeIcon /> : <CloseEyeIcon />}
          </StyledActionButton>
          <StyledStatusButton $isActive={isActive} type={isActive ? 'text' : 'default'} onClick={handleStatusChange}>
            {isActive ? <CloseFullCircleIcon /> : <CheckmarkCircleIcon />}
            {t(userStatusButtonText)}
          </StyledStatusButton>
        </>
      )}
      {canLoginAsPartner && (
        <StyledButton onClick={() => authAsPartner(id)}>{t('users_list_enter_button')}</StyledButton>
      )}
    </StyledActionsWrap>
  );
};

export default PartnerTableActions;
